import React, { useState, useCallback, useMemo } from 'react';
import {
    List,
    ListItem,
    ListItemButton,
    ListItemIcon,
    ListItemText,
    Collapse,
    Box,
    Drawer,
    Typography,
    IconButton,
    Tooltip,
    alpha
} from '@mui/material';
import { useNavigate, useLocation } from 'react-router-dom';

// Hooks
import { useAuth } from '../../hooks/useAuth';

// Icons
import DashboardIcon from '@mui/icons-material/DashboardOutlined';
import StorageIcon from '@mui/icons-material/StorageOutlined';
import TerminalIcon from '@mui/icons-material/TerminalOutlined';
import SpeedIcon from '@mui/icons-material/SpeedOutlined';
import NotificationsNoneIcon from '@mui/icons-material/NotificationsNoneOutlined';
import SettingsOutlinedIcon from '@mui/icons-material/SettingsOutlined';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import AssessmentOutlinedIcon from '@mui/icons-material/AssessmentOutlined';
import TuneIcon from '@mui/icons-material/TuneOutlined';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import AdminPanelSettingsOutlinedIcon from '@mui/icons-material/AdminPanelSettingsOutlined';
import { tokens } from '../../styles/tokens';
import { HEADER_H } from './headerSlot';

// Static nav items moved outside component
const NAV_ITEMS = [
    { name: 'Query Runner', path: '/query_database', icon: <TerminalIcon /> }
];

const ADMIN_PATHS = ['/admin'];

// The collapsed rail's buttons, sized with the top bar's bell and sign-out
// (30px) so the chrome reads as one set.
// flex: none, because a ListItemButton grows to fill its row and ignores the width
const RAIL_SQUARE = { width: 32, height: 32, flex: 'none', minHeight: 0, p: 0, mx: 'auto', borderRadius: '8px', justifyContent: 'center' };
const RAIL_ICON = 18;

const CLUSTERS_SUB_ITEMS = [
    { name: 'Overview', path: '/dashboard', icon: <DashboardIcon /> },
    { name: 'Settings', path: '/clusters', icon: <SettingsOutlinedIcon /> },
];

const CLUSTERS_PATHS = ['/dashboard', '/clusters'];

const PERFORMANCE_SUB_ITEMS = [
    { name: 'Notifications', path: '/notifications', icon: <NotificationsNoneIcon /> },
    { name: 'Settings', path: '/notification_settings', icon: <SettingsOutlinedIcon /> },
    { name: 'Thresholds', path: '/notifications/thresholds', icon: <TuneIcon /> },
    { name: 'Score Analysis', path: '/performance_alerts', icon: <AssessmentOutlinedIcon /> },
];

const PERFORMANCE_PATHS = ['/performance_alerts', '/notifications', '/notification_settings'];

const Sidebar = ({ width = 260, mobileOpen, onDrawerToggle, collapsed = false, onCollapse }) => {
    const navigate = useNavigate();
    const location = useLocation();
    const { isAdmin, canAccessAdmin } = useAuth();
    const [clustersOpen, setClustersOpen] = useState(false);
    const [performanceOpen, setPerformanceOpen] = useState(false);

    // Memoize path checks
    const isSelected = useCallback((path) => location.pathname === path, [location.pathname]);
    const isInClustersSection = useMemo(
        () => CLUSTERS_PATHS.some(p => location.pathname === p || location.pathname.startsWith('/dashboard/')),
        [location.pathname]
    );
    const isInPerformanceSection = useMemo(
        () => PERFORMANCE_PATHS.some(p => location.pathname.startsWith(p)),
        [location.pathname]
    );

    // Memoize navigation handler
    const handleNavigate = useCallback((path) => {
        navigate(path);
    }, [navigate]);

    // Memoize toggle handlers
    const handleClustersToggle = useCallback(() => {
        if (!collapsed) {
            setClustersOpen(prev => !prev);
        }
    }, [collapsed]);

    const handlePerformanceToggle = useCallback(() => {
        if (!collapsed) {
            setPerformanceOpen(prev => !prev);
        }
    }, [collapsed]);

    const drawerContent = (showBrand) => (
        <Box sx={{
            height: '100%',
            display: 'flex',
            flexDirection: 'column',
            backgroundColor: '#ffffff',
        }}>
            {/* Brand Area: the phone menu only. On desktop the top bar spans the
                page and carries the logo and name, so the sidebar starts below it. */}
            {showBrand && (
                <Box sx={{
                    px: collapsed ? 1.5 : 2,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    height: tokens.sizes.topbarHeight + 1,
                    boxSizing: 'border-box',
                    borderBottom: '1px solid #F1F5F9',
                }}>
                    <Box
                        onClick={() => handleNavigate('/dashboard')}
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 1.5,
                            cursor: 'pointer',
                            flex: 1,
                            justifyContent: collapsed ? 'center' : 'flex-start',
                            '&:hover': {
                                opacity: 0.85,
                            },
                        }}
                    >
                        <Box
                            component="img"
                            src={`${process.env.PUBLIC_URL}/lightbeam-logo.svg`}
                            alt="Lightbeam"
                            sx={{
                                height: collapsed ? 28 : 32,
                                objectFit: 'contain',
                                flexShrink: 0,
                            }}
                        />
                        {!collapsed && (
                            <Typography
                                variant="h6"
                                sx={{
                                    fontWeight: 700,
                                    fontSize: '1.125rem',
                                    color: '#0F172A',
                                    letterSpacing: '-0.02em',
                                }}
                            >
                                Watchtower
                            </Typography>
                        )}
                    </Box>
                    {/* Collapse button - prominent placement */}
                    {!collapsed && (
                        <IconButton
                            onClick={onCollapse}
                            aria-label="Collapse sidebar"
                            size="small"
                            sx={{
                                color: '#64748B',
                                backgroundColor: '#F8FAFC',
                                border: '1px solid #E2E8F0',
                                width: 28,
                                height: 28,
                                '&:hover': {
                                    backgroundColor: '#F1F5F9',
                                    color: '#0F172A',
                                    borderColor: '#CBD5E1',
                                },
                            }}
                        >
                            <ChevronLeftIcon sx={{ fontSize: 18 }} />
                        </IconButton>
                    )}
                </Box>
            )}

            {/* Navigation */}
            <List sx={{ px: collapsed ? 0.5 : 1.5, pt: 2, flexGrow: 1, overflowY: 'auto' }}>
                {/* Clusters Section */}
                {collapsed ? (
                    /* Collapsed: Section label (non-clickable, bold, grey) */
                    <Tooltip title="Clusters (Section)" placement="right" arrow>
                        <Box sx={{
                            py: 0.75,
                            px: 0.5,
                            mb: 0.5,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            cursor: 'default',
                        }}>
                            <Box sx={{
                                ...RAIL_SQUARE,
                                backgroundColor: '#F1F5F9',
                                display: 'flex',
                                alignItems: 'center',
                                border: '1px solid #E2E8F0',
                            }}>
                                <StorageIcon sx={{ color: '#64748B', fontSize: RAIL_ICON }} />
                            </Box>
                        </Box>
                    </Tooltip>
                ) : (
                    /* Expanded: Clickable section header */
                    <ListItem disablePadding sx={{ mb: 0.5 }}>
                        <ListItemButton
                            onClick={handleClustersToggle}
                            sx={{
                                borderRadius: 2,
                                py: 1.25,
                                px: 2,
                                transition: 'all 0.15s ease',
                                '&:hover': {
                                    backgroundColor: '#F1F5F9',
                                },
                            }}
                        >
                            <ListItemIcon sx={{
                                minWidth: 40,
                                justifyContent: 'center',
                                color: isInClustersSection ? '#2563EB' : '#64748B'
                            }}>
                                <StorageIcon />
                            </ListItemIcon>
                            <ListItemText
                                primary="Clusters"
                                primaryTypographyProps={{
                                    fontSize: '0.875rem',
                                    fontWeight: isInClustersSection ? 600 : 500,
                                    color: isInClustersSection ? '#2563EB' : '#0F172A',
                                }}
                            />
                            {clustersOpen ? (
                                <ExpandLessIcon sx={{ color: '#94A3B8', fontSize: 20 }} />
                            ) : (
                                <ExpandMoreIcon sx={{ color: '#94A3B8', fontSize: 20 }} />
                            )}
                        </ListItemButton>
                    </ListItem>
                )}

                {/* Clusters Sub-items */}
                {collapsed ? (
                    <>
                        {/* Visual connector line for sub-items */}
                        <Box sx={{
                            display: 'flex',
                            justifyContent: 'center',
                            mb: 0.5,
                        }}>
                            <Box sx={{
                                width: 2,
                                height: 8,
                                backgroundColor: '#E2E8F0',
                                borderRadius: 1,
                            }} />
                        </Box>
                        {CLUSTERS_SUB_ITEMS.map((subItem, idx) => {
                            const isActive = isSelected(subItem.path) || (subItem.path === '/dashboard' && location.pathname.startsWith('/dashboard/'));
                            return (
                                <ListItem key={subItem.name} disablePadding sx={{ mb: 0.5 }}>
                                    <Tooltip title={subItem.name} placement="right" arrow>
                                        <ListItemButton
                                            selected={isActive}
                                            onClick={() => handleNavigate(subItem.path)}
                                            sx={{
                                                ...RAIL_SQUARE,
                                                backgroundColor: isActive ? '#EFF6FF' : 'transparent',
                                                border: isActive ? '1px solid' : '1px solid transparent',
                                                borderColor: isActive ? alpha('#2563EB', 0.45) : 'transparent',
                                                '&:hover': {
                                                    backgroundColor: isActive ? '#DBEAFE' : '#F8FAFC',
                                                    borderColor: '#E2E8F0',
                                                },
                                            }}
                                        >
                                            <ListItemIcon sx={{
                                                minWidth: 0,
                                                color: isActive ? '#1D4ED8' : '#64748B',
                                                '& svg': { fontSize: RAIL_ICON }
                                            }}>
                                                {subItem.icon}
                                            </ListItemIcon>
                                        </ListItemButton>
                                    </Tooltip>
                                </ListItem>
                            );
                        })}
                        {/* Separator after section */}
                        <Box sx={{
                            mx: 1,
                            my: 1.5,
                            borderBottom: '1px solid #E2E8F0',
                        }} />
                    </>
                ) : (
                    <Collapse in={clustersOpen} timeout="auto" unmountOnExit>
                        <List component="div" disablePadding sx={{ pl: 1 }}>
                            {CLUSTERS_SUB_ITEMS.map((subItem) => {
                                const isSubActive = isSelected(subItem.path) || (subItem.path === '/dashboard' && location.pathname.startsWith('/dashboard/'));
                                return (
                                    <ListItemButton
                                        key={subItem.name}
                                        selected={isSubActive}
                                        onClick={() => handleNavigate(subItem.path)}
                                        sx={{
                                            pl: 4.5,
                                            py: 0.75,
                                            mb: 0.25,
                                            borderRadius: 1.5,
                                            '&:hover': { backgroundColor: '#F1F5F9' },
                                            '&.Mui-selected': {
                                                backgroundColor: '#EFF6FF',
                                                boxShadow: 'inset 3px 0 #2563EB',
                                                '&:hover': { backgroundColor: '#DBEAFE' },
                                            },
                                        }}
                                    >
                                        <ListItemIcon sx={{
                                            minWidth: 32,
                                            color: isSubActive ? '#1D4ED8' : '#94A3B8',
                                            '& svg': { fontSize: 18 }
                                        }}>
                                            {subItem.icon}
                                        </ListItemIcon>
                                        <ListItemText
                                            primary={subItem.name}
                                            primaryTypographyProps={{
                                                fontSize: '0.8125rem',
                                                fontWeight: isSubActive ? 600 : 500,
                                                color: isSubActive ? '#1D4ED8' : '#475569',
                                            }}
                                        />
                                    </ListItemButton>
                                );
                            })}
                        </List>
                    </Collapse>
                )}

                {NAV_ITEMS.map((item) => {
                    const isActive = isSelected(item.path);
                    return (
                        <ListItem key={item.name} disablePadding sx={{ mb: 0.5 }}>
                            <Tooltip title={collapsed ? item.name : ''} placement="right" arrow>
                                <ListItemButton
                                    selected={isActive}
                                    onClick={() => handleNavigate(item.path)}
                                    sx={{
                                        borderRadius: 2,
                                        py: 1,
                                        px: 2,
                                        justifyContent: 'flex-start',
                                        ...(collapsed && RAIL_SQUARE),
                                        transition: 'all 0.15s ease',
                                        backgroundColor: isActive ? '#2563EB' : 'transparent',
                                        border: collapsed ? '1px solid transparent' : 'none',
                                        '&:hover': {
                                            backgroundColor: isActive ? '#1D4ED8' : '#F8FAFC',
                                            borderColor: collapsed ? '#E2E8F0' : 'transparent',
                                        },
                                    }}
                                >
                                    <ListItemIcon sx={{
                                        minWidth: collapsed ? 0 : 40,
                                        justifyContent: 'center',
                                        ...(collapsed && { '& svg': { fontSize: RAIL_ICON } }),
                                        color: isActive ? '#ffffff' : '#64748B',
                                    }}>
                                        {item.icon}
                                    </ListItemIcon>
                                    {!collapsed && (
                                        <ListItemText
                                            primary={item.name}
                                            primaryTypographyProps={{
                                                fontSize: '0.875rem',
                                                fontWeight: isActive ? 600 : 500,
                                                color: isActive ? '#ffffff' : '#0F172A',
                                            }}
                                        />
                                    )}
                                </ListItemButton>
                            </Tooltip>
                        </ListItem>
                    );
                })}

                {/* Performance Section */}
                {collapsed ? (
                    /* Collapsed: Section label (non-clickable, bold, grey) */
                    <Tooltip title="Performance (Section)" placement="right" arrow>
                        <Box sx={{
                            py: 0.75,
                            px: 0.5,
                            mt: 1,
                            mb: 0.5,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            cursor: 'default',
                        }}>
                            <Box sx={{
                                ...RAIL_SQUARE,
                                backgroundColor: '#F1F5F9',
                                display: 'flex',
                                alignItems: 'center',
                                border: '1px solid #E2E8F0',
                            }}>
                                <SpeedIcon sx={{ color: '#64748B', fontSize: RAIL_ICON }} />
                            </Box>
                        </Box>
                    </Tooltip>
                ) : (
                    /* Expanded: Clickable section header */
                    <ListItem disablePadding sx={{ mb: 0.5, mt: 1 }}>
                        <ListItemButton
                            onClick={handlePerformanceToggle}
                            sx={{
                                borderRadius: 2,
                                py: 1.25,
                                px: 2,
                                transition: 'all 0.15s ease',
                                '&:hover': {
                                    backgroundColor: '#F1F5F9',
                                },
                            }}
                        >
                            <ListItemIcon sx={{
                                minWidth: 40,
                                justifyContent: 'center',
                                color: isInPerformanceSection ? '#2563EB' : '#64748B'
                            }}>
                                <SpeedIcon />
                            </ListItemIcon>
                            <ListItemText
                                primary="Performance"
                                primaryTypographyProps={{
                                    fontSize: '0.875rem',
                                    fontWeight: isInPerformanceSection ? 600 : 500,
                                    color: isInPerformanceSection ? '#2563EB' : '#0F172A',
                                }}
                            />
                            {performanceOpen ? (
                                <ExpandLessIcon sx={{ color: '#94A3B8', fontSize: 20 }} />
                            ) : (
                                <ExpandMoreIcon sx={{ color: '#94A3B8', fontSize: 20 }} />
                            )}
                        </ListItemButton>
                    </ListItem>
                )}

                {/* Performance Sub-items */}
                {collapsed ? (
                    <>
                        {/* Visual connector line for sub-items */}
                        <Box sx={{
                            display: 'flex',
                            justifyContent: 'center',
                            mb: 0.5,
                        }}>
                            <Box sx={{
                                width: 2,
                                height: 8,
                                backgroundColor: '#E2E8F0',
                                borderRadius: 1,
                            }} />
                        </Box>
                        {PERFORMANCE_SUB_ITEMS.map((subItem) => {
                            const isActive = isSelected(subItem.path);
                            return (
                                <ListItem key={subItem.name} disablePadding sx={{ mb: 0.5 }}>
                                    <Tooltip title={subItem.name} placement="right" arrow>
                                        <ListItemButton
                                            selected={isActive}
                                            onClick={() => handleNavigate(subItem.path)}
                                            sx={{
                                                ...RAIL_SQUARE,
                                                backgroundColor: isActive ? '#EFF6FF' : 'transparent',
                                                border: isActive ? '1px solid' : '1px solid transparent',
                                                borderColor: isActive ? alpha('#2563EB', 0.45) : 'transparent',
                                                '&:hover': {
                                                    backgroundColor: isActive ? '#DBEAFE' : '#F8FAFC',
                                                    borderColor: '#E2E8F0',
                                                },
                                            }}
                                        >
                                            <ListItemIcon sx={{
                                                minWidth: 0,
                                                color: isActive ? '#1D4ED8' : '#64748B',
                                                '& svg': { fontSize: RAIL_ICON }
                                            }}>
                                                {subItem.icon}
                                            </ListItemIcon>
                                        </ListItemButton>
                                    </Tooltip>
                                </ListItem>
                            );
                        })}
                    </>
                ) : (
                    // Expanded: show in collapse
                    <Collapse in={performanceOpen} timeout="auto" unmountOnExit>
                        <List component="div" disablePadding sx={{ pl: 1 }}>
                            {PERFORMANCE_SUB_ITEMS.map((subItem) => (
                                <ListItemButton
                                    key={subItem.name}
                                    selected={isSelected(subItem.path)}
                                    onClick={() => handleNavigate(subItem.path)}
                                    sx={{
                                        pl: 4.5,
                                        py: 0.75,
                                        mb: 0.25,
                                        borderRadius: 1.5,
                                        '&:hover': { backgroundColor: '#F1F5F9' },
                                        '&.Mui-selected': {
                                            backgroundColor: '#EFF6FF',
                                            boxShadow: 'inset 3px 0 #2563EB',
                                            '&:hover': { backgroundColor: '#DBEAFE' },
                                        },
                                    }}
                                >
                                    <ListItemIcon sx={{
                                        minWidth: 32,
                                        color: isSelected(subItem.path) ? '#1D4ED8' : '#94A3B8',
                                        '& svg': { fontSize: 18 }
                                    }}>
                                        {subItem.icon}
                                    </ListItemIcon>
                                    <ListItemText
                                        primary={subItem.name}
                                        primaryTypographyProps={{
                                            fontSize: '0.8125rem',
                                            fontWeight: isSelected(subItem.path) ? 600 : 500,
                                            color: isSelected(subItem.path) ? '#1D4ED8' : '#475569',
                                        }}
                                    />
                                </ListItemButton>
                            ))}
                        </List>
                    </Collapse>
                )}
                {/* Admin Item - visible to admins and CSE users */}
                {canAccessAdmin && (
                    <>
                        <Box sx={{ mx: 1, my: 1.5, borderBottom: '1px solid #E2E8F0' }} />
                        <ListItem disablePadding sx={{ mb: 0.5 }}>
                            <Tooltip title={collapsed ? 'Admin' : ''} placement="right" arrow>
                                <ListItemButton
                                    selected={ADMIN_PATHS.some(p => location.pathname.startsWith(p))}
                                    onClick={() => handleNavigate('/admin')}
                                    sx={{
                                        borderRadius: 2,
                                        py: 1,
                                        px: 2,
                                        justifyContent: 'flex-start',
                                        ...(collapsed && RAIL_SQUARE),
                                        transition: 'all 0.15s ease',
                                        '&:hover': { backgroundColor: '#F8FAFC' },
                                        '&.Mui-selected': {
                                            backgroundColor: '#EFF6FF',
                                            '&:hover': { backgroundColor: '#DBEAFE' },
                                        },
                                    }}
                                >
                                    <ListItemIcon sx={{
                                        minWidth: collapsed ? 0 : 40,
                                        justifyContent: 'center',
                                        ...(collapsed && { '& svg': { fontSize: RAIL_ICON } }),
                                        color: ADMIN_PATHS.some(p => location.pathname.startsWith(p)) ? '#2563EB' : '#64748B',
                                    }}>
                                        <AdminPanelSettingsOutlinedIcon />
                                    </ListItemIcon>
                                    {!collapsed && (
                                        <ListItemText
                                            primary="Admin"
                                            primaryTypographyProps={{
                                                fontSize: '0.875rem',
                                                fontWeight: ADMIN_PATHS.some(p => location.pathname.startsWith(p)) ? 600 : 500,
                                                color: ADMIN_PATHS.some(p => location.pathname.startsWith(p)) ? '#2563EB' : '#0F172A',
                                            }}
                                        />
                                    )}
                                </ListItemButton>
                            </Tooltip>
                        </ListItem>
                    </>
                )}
            </List>

            {/* Footer */}
            <Box sx={{
                p: 1.5,
                borderTop: '1px solid #F1F5F9',
                display: 'flex',
                alignItems: 'center',
                justifyContent: collapsed ? 'center' : 'space-between',
            }}>
                {collapsed ? (
                    /* Collapsed: Prominent expand button */
                    <Tooltip title="Expand sidebar" placement="right" arrow>
                        <IconButton
                            onClick={onCollapse}
                            aria-label="Expand sidebar"
                            sx={{
                                color: '#fff',
                                backgroundColor: '#2563EB',
                                width: 30,
                                height: 30,
                                boxShadow: '0 1px 4px rgba(37, 99, 235, 0.3)',
                                '&:hover': {
                                    backgroundColor: '#1D4ED8',
                                },
                            }}
                        >
                            <ChevronRightIcon sx={{ fontSize: RAIL_ICON }} />
                        </IconButton>
                    </Tooltip>
                ) : (
                    <>
                        <Typography variant="caption" sx={{ color: '#94A3B8', fontSize: '0.6875rem' }}>
                            v0.1.0
                        </Typography>
                        <Tooltip title="Collapse sidebar" placement="right" arrow>
                            <IconButton
                                onClick={onCollapse}
                                aria-label="Collapse sidebar"
                                size="small"
                                sx={{
                                    color: '#64748B',
                                    backgroundColor: '#F8FAFC',
                                    border: '1px solid #E2E8F0',
                                    width: 28,
                                    height: 28,
                                    '&:hover': { backgroundColor: '#F1F5F9', color: '#0F172A', borderColor: '#CBD5E1' },
                                }}
                            >
                                <ChevronLeftIcon sx={{ fontSize: 18 }} />
                            </IconButton>
                        </Tooltip>
                    </>
                )}
            </Box>
        </Box>
    );

    return (
        <>
            {/* Mobile Drawer */}
            <Drawer
                variant="temporary"
                open={mobileOpen}
                onClose={onDrawerToggle}
                ModalProps={{ keepMounted: true }}
                sx={{
                    display: { xs: 'block', md: 'none' },
                    '& .MuiDrawer-paper': {
                        boxSizing: 'border-box',
                        width: 260,
                        border: 'none',
                    },
                }}
            >
                {drawerContent(true)}
            </Drawer>

            {/* Desktop Drawer */}
            <Drawer
                variant="permanent"
                sx={{
                    display: { xs: 'none', md: 'flex' },
                    width: width,
                    flexShrink: 0,
                    transition: 'width 0.2s ease',
                    '& .MuiDrawer-paper': {
                        boxSizing: 'border-box',
                        width: width,
                        top: HEADER_H,
                        height: `calc(100% - ${HEADER_H})`,
                        borderRight: '1px solid #E2E8F0',
                        transition: 'width 0.2s ease, top 0.2s ease',
                    },
                }}
                open
            >
                {drawerContent(false)}
            </Drawer>
        </>
    );
};

export default Sidebar;
